import { IdentityUserAri } from '@atlassian/ari/identity/user';

import { OPTIMISTIC_AGENT_REPLY_ID_PREFIX } from './agentMentionConstants.ts';

type Account = { accountId?: string | null };
export type ReplyMatchComment = {
	id: string;
	parentId: string | null;
	createdAtNonLocalized: string;
	author: Account;
	contributors: readonly (Account | null)[] | null;
};

export type PostedReplyMatch = {
	commentId: string;
	userId: string;
	agentId: string;
	startedAt: number;
	existingCommentIds: ReadonlySet<string>;
};

const threadRoot = (id: string, comments: Record<string, ReplyMatchComment>): string => {
	const visited = new Set<string>();
	let parentId = comments[id]?.parentId;
	while (parentId && !visited.has(id)) {
		visited.add(id);
		id = parentId;
		parentId = comments[id]?.parentId;
	}
	return id;
};

const isReplyFromAgentAndUser = (comment: ReplyMatchComment, match: PostedReplyMatch): boolean => {
	const author = comment.author.accountId;
	const contributors = comment.contributors ?? [];
	return (
		(author === match.agentId && contributors.some((user) => user?.accountId === match.userId)) ||
		(author === match.userId && contributors.some((user) => user?.accountId === match.agentId))
	);
};

export function findPostedCommentAgentReply(
	comments: Record<string, ReplyMatchComment>,
	match: PostedReplyMatch,
): ReplyMatchComment | undefined {
	// Session agents use identity ARIs; comment attribution uses account IDs.
	let agentId = match.agentId;
	try {
		agentId = IdentityUserAri.parse(agentId).userId;
	} catch {
		// Optimistic identities already contain a plain account ID.
	}
	const normalizedMatch = { ...match, agentId };
	const rootId = threadRoot(match.commentId, comments);
	return Object.values(comments).find((comment) => {
		if (comment.id.startsWith(OPTIMISTIC_AGENT_REPLY_ID_PREFIX)) return false;
		if (!comment.parentId || match.existingCommentIds.has(comment.id)) return false;
		// Exclude historical comments loaded later, allowing a small server/client clock difference.
		if (!(Date.parse(comment.createdAtNonLocalized) >= match.startedAt - 30_000)) return false;
		if (threadRoot(comment.id, comments) !== rootId) return false;
		return isReplyFromAgentAndUser(comment, normalizedMatch);
	});
}

export function hasPostedCommentAgentReply(
	comments: Record<string, ReplyMatchComment>,
	match: PostedReplyMatch,
): boolean {
	return Boolean(findPostedCommentAgentReply(comments, match));
}
